/* eslint-disable react-refresh/only-export-components -- Context files intentionally export both a context object and a hook from the same module. Splitting would break the encapsulation of this pattern. */
/**
 * Mixpanel Context Provider
 *
 * Provides Mixpanel analytics via React Context for dependency injection.
 * This pattern allows easy mocking in tests and Storybook stories.
 *
 */

import { createContext, useContext, useMemo, ReactNode } from 'react';
import { MixpanelManager, initializeMixpanel } from '@/config/mixpanel';

export type MixpanelContextData = {
  mixpanel: MixpanelManager;
};

const createDefaultMixpanel = (): MixpanelManager => {
  const manager = new MixpanelManager();
  return manager;
};

const defaultState: MixpanelContextData = {
  mixpanel: createDefaultMixpanel(),
};

export const MixpanelContext = createContext<MixpanelContextData>(defaultState);

interface MixpanelProviderProps {
  children: ReactNode;
  /** Optional: Inject a custom MixpanelManager instance (useful for testing) */
  mixpanelManager?: MixpanelManager;
}

/**
 * MixpanelProvider - Wraps the app to provide Mixpanel analytics via context
 *
 * In production, this initializes Mixpanel and provides the manager instance
 * In tests, you can inject a mock manager via the mixpanelManager prop.
 */
export function MixpanelProvider({ children, mixpanelManager }: MixpanelProviderProps) {
  const mixpanel = useMemo<MixpanelManager>(() => {
    if (mixpanelManager) {
      return mixpanelManager;
    }

    initializeMixpanel();
    return new MixpanelManager();
  }, [mixpanelManager]);

  return <MixpanelContext.Provider value={{ mixpanel }}>{children}</MixpanelContext.Provider>;
}

/**
 * useMixpanel - Hook to access Mixpanel analytics
 * @returns MixpanelContextData containing the mixpanel manager instance
 */
export function useMixpanel(): MixpanelContextData {
  const context = useContext(MixpanelContext);

  if (!context) {
    throw new Error('useMixpanel must be used within a MixpanelProvider');
  }

  return context;
}

export default MixpanelProvider;
